import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { useSnackbar } from "notistack";

import TableComponent from "../../components/table";
import ButtonWithLoading from "../../components/ButtonWithLoading";
import { Get } from "../../actions";
import {
  Get_CronJobCustomers_URL,
  Get_RunCronJobNow_URL,
} from "../../constants/apiUrls";
import { getTranslation } from "../../heplers/translationHelper";
import { format } from "../../heplers/format";

/**
 * Colour by last outcome. "Never" is deliberately not green or red: the job has not been tried for
 * that customer at all, which is neither a success nor a failure.
 */
const statusStyle = (customer) => {
  if (customer?.hasNeverRun)
    return { backgroundColor: "#ede7f6", color: "#4527a0" };

  switch (customer?.lastStatusName) {
    case "Succeeded":
      return { backgroundColor: "#e6f4ea", color: "#1b5e20" };
    case "Failed":
      return { backgroundColor: "#fdecea", color: "#b71c1c" };
    case "Skipped":
      return { backgroundColor: "#fff4e5", color: "#8a5300" };
    default:
      return { backgroundColor: "#eeeeee", color: "#424242" };
  }
};

const statusLabel = (customer) => {
  if (customer?.hasNeverRun)
    return getTranslation("Never ran", "Jamais exécuté", "Nie gelaufen");
  return customer?.lastStatusName;
};

/**
 * Per-customer state of one cron job: was it triggered for this customer, did it work, and how
 * often has it been failing.
 */
const CronJobCustomers = ({ cronJob, onOpenCustomer }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [result, setResult] = useState(null);
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState("");
  const [searchString, setSearchString] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [runningCustomerId, setRunningCustomerId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    getCustomers();
    // eslint-disable-next-line
  }, [cronJob?.id, days]);

  const getCustomers = () => {
    setIsLoading(true);
    const params = { id: cronJob.id, days: days };
    if (searchString) params.searchString = searchString;
    if (status !== "") params.status = status;

    Get(
      params,
      Get_CronJobCustomers_URL,
      null,
      (resp) => {
        setResult(resp.data);
        setPage(0);
        setIsLoading(false);
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setIsLoading(false);
      }
    );
  };

  const runForCustomer = (customer) => {
    setRunningCustomerId(customer.customerId);
    Get(
      { id: cronJob.id, customerId: customer.customerId },
      Get_RunCronJobNow_URL,
      null,
      (resp) => {
        const summary = resp?.data;
        enqueueSnackbar(
          `${customer.customerId}: ${
            summary?.failed > 0
              ? getTranslation("failed", "échoué", "fehlgeschlagen")
              : summary?.succeeded > 0
              ? getTranslation("succeeded", "réussi", "erfolgreich")
              : getTranslation("skipped", "ignoré", "übersprungen")
          }`,
          { variant: summary?.failed > 0 ? "error" : "success" }
        );
        setRunningCustomerId(null);
        getCustomers();
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setRunningCustomerId(null);
      }
    );
  };

  const columns = [
    {
      id: "organizationName",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Organization", "Organisation", "Organisation"),
      component: ({ row }) => (
        <Box>
          <Typography noWrap>{row?.organizationName || "-"}</Typography>
          <Typography variant="caption" color="textSecondary" noWrap>
            {row?.customerId}
          </Typography>
        </Box>
      ),
    },
    {
      id: "lastStatusName",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Last result", "Dernier résultat", "Letztes Ergebnis"),
      component: ({ row }) => (
        <Tooltip title={row?.lastMessage || ""}>
          <Chip size="small" label={statusLabel(row)} style={statusStyle(row)} />
        </Tooltip>
      ),
    },
    {
      id: "lastRunAt",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Last run", "Dernière exécution", "Letzter Lauf"),
      component: ({ row }) => (
        <Box>
          <Typography noWrap>
            {row?.lastRunAt ? format("dateTime", row.lastRunAt) : "-"}
          </Typography>
          {row?.lastStatusCode ? (
            <Typography variant="caption" color="textSecondary">
              HTTP {row.lastStatusCode}
              {row.lastAttempts > 1
                ? ` · ${row.lastAttempts} ${getTranslation(
                    "attempts",
                    "tentatives",
                    "Versuche"
                  )}`
                : ""}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      id: "counts",
      numeric: false,
      disablePadding: true,
      label: getTranslation("In period", "Période", "Zeitraum"),
      component: ({ row }) => (
        <Box display="flex">
          <Box mr={1}>
            <Chip
              size="small"
              label={`${row?.succeededCount ?? 0} ✓`}
              style={{ backgroundColor: "#e6f4ea", color: "#1b5e20" }}
            />
          </Box>
          {row?.failedCount > 0 ? (
            <Box mr={1}>
              <Chip
                size="small"
                label={`${row.failedCount} ✕`}
                style={{ backgroundColor: "#fdecea", color: "#b71c1c" }}
              />
            </Box>
          ) : null}
          {row?.skippedCount > 0 ? (
            <Chip
              size="small"
              label={`${row.skippedCount} –`}
              style={{ backgroundColor: "#fff4e5", color: "#8a5300" }}
            />
          ) : null}
        </Box>
      ),
    },
    {
      id: "isActive",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Customer", "Client", "Kunde"),
      component: ({ row }) => (
        <Chip
          size="small"
          label={
            row?.isActive
              ? getTranslation("Active", "Actif", "Aktiv")
              : getTranslation("Expired", "Expiré", "Abgelaufen")
          }
          style={
            row?.isActive
              ? { backgroundColor: "#e6f4ea", color: "#1b5e20" }
              : { backgroundColor: "#eeeeee", color: "#424242" }
          }
        />
      ),
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      align: "right",
      label: getTranslation("Actions", "Actions", "Aktionen"),
    },
  ];

  const customers = result?.customers ?? [];
  const paged = customers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box mb={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Search", "Rechercher", "Suchen")}
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") getCustomers();
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Last result", "Dernier résultat", "Letztes Ergebnis")}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">
                {getTranslation("All", "Tous", "Alle")}
              </MenuItem>
              <MenuItem value={1}>
                {getTranslation("Succeeded", "Réussi", "Erfolgreich")}
              </MenuItem>
              <MenuItem value={2}>
                {getTranslation("Failed", "Échoué", "Fehlgeschlagen")}
              </MenuItem>
              <MenuItem value={3}>
                {getTranslation("Skipped", "Ignoré", "Übersprungen")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Period", "Période", "Zeitraum")}
              value={days}
              onChange={(e) => setDays(e.target.value)}
            >
              <MenuItem value={7}>7</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={90}>90</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <ButtonWithLoading
              label={getTranslation("Filter", "Filtrer", "Filtern")}
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => getCustomers()}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2">
              {result?.total ?? 0}{" "}
              {getTranslation("customers", "clients", "Kunden")}
              {result?.failed > 0 ? (
                <b>
                  {" "}
                  · {result.failed}{" "}
                  {getTranslation("failing", "en échec", "fehlerhaft")}
                </b>
              ) : null}
              {result?.neverRun > 0 ? (
                <>
                  {" "}
                  · {result.neverRun}{" "}
                  {getTranslation("never ran", "jamais exécuté", "nie gelaufen")}
                </>
              ) : null}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <TableComponent
        dense
        loading={isLoading}
        colums={columns}
        count={customers.length}
        rows={paged}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={(event, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        actions={[
          {
            component: (rowData) => (
              <ButtonWithLoading
                label={getTranslation("History", "Historique", "Verlauf")}
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => onOpenCustomer(rowData)}
              />
            ),
          },
          {
            component: (rowData) => (
              <ButtonWithLoading
                label={getTranslation("Run now", "Exécuter", "Jetzt starten")}
                size="small"
                variant="contained"
                color="primary"
                loading={runningCustomerId === rowData.customerId}
                onClick={() => runForCustomer(rowData)}
              />
            ),
          },
        ]}
      />
    </Box>
  );
};

export default CronJobCustomers;
